import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactCard } from '~/components/cards/ContactCard';
import { AddSellerContactPanel } from '~/components/common/AddSellerContactPanel';
import { SelectAllCheckbox } from '~/components/common/SelectAllCheckbox';
import { useSellerContactsPage } from '~/components/common/useSellerContactsPage';
import { Button, ClipSpinner } from '~/components/ui';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { cn } from '~/utils';
import type { ContactDetails, SavedContact } from '~/utils/contact';

export type PrimaryContactMode = 'required' | 'optional' | 'off';

type ContactsPaging = Readonly<{
  sentinelRef: (node: HTMLDivElement | null) => void;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadFailed: boolean;
  retry: () => void;
}>;

type PrimaryContactProps =
  | Readonly<{
      primaryContactMode: 'required';
      primaryRequiredMessage: string;
    }>
  | Readonly<{
      primaryContactMode: Exclude<PrimaryContactMode, 'required'>;
      primaryRequiredMessage?: never;
    }>;

type ListedContactsPickerProps = Readonly<{
  id: string;
  organizationId: string;
  sellerName: string;
  selectedContactIds: string[];
  primaryContactId: string | undefined;
  lockedContactIds?: string[];
  lockedLabel?: string;
  onChange: (
    selectedContacts: ContactDetails[],
    primaryContactId: string | undefined
  ) => void;
  onContactAdded?: (contact: SavedContact) => void;
  addFormWhenEmpty?: Readonly<{ onCancel: () => void }>;
  footer?: (selection: ListedSelection) => ReactNode;
  layout?: 'inline' | 'modal';
  disabled: boolean;
  testId: string;
}> &
  PrimaryContactProps;

type SellerContactsPickerProps = ListedContactsPickerProps &
  Readonly<{
    contacts: ContactDetails[];
    paging?: ContactsPaging;
  }>;

type ListedSelection = Readonly<{
  selectedContacts: ContactDetails[];
  primaryContact: ContactDetails | undefined;
}>;

type SelectContactsToggleProps = Readonly<{
  expanded: boolean;
  onToggle: () => void;
  controlsId: string;
  sellerName: string;
  disabled: boolean;
  testId: string;
}>;

const NO_CONTACT_IDS: string[] = [];

export function SelectContactsToggle({
  expanded,
  onToggle,
  controlsId,
  sellerName,
  disabled,
  testId,
}: SelectContactsToggleProps) {
  const { t } = useTranslation('general');
  return (
    <Button
      variant="secondary"
      size="sm"
      width="auto"
      className={cn(
        'shrink-0 whitespace-nowrap',
        expanded ? undefined : 'border border-neut-border'
      )}
      disabled={disabled}
      aria-expanded={expanded}
      aria-controls={controlsId}
      aria-label={t('seller_contacts_summary.select_contacts_aria_label', {
        sellerName,
      })}
      data-testid={testId}
      onClick={onToggle}
    >
      {t('seller_contacts_summary.select_contacts')}
    </Button>
  );
}

const pickerClassNames = {
  inline:
    '@container relative flex min-w-0 flex-col gap-3 border-t border-neut-border bg-neut-bg-alt px-4 py-3 text-left font-normal',
  modal: 'contents text-left font-normal',
};

const modalBodyClassName =
  'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-4';

export function SellerContactsPicker({
  id,
  organizationId,
  sellerName,
  contacts,
  selectedContactIds,
  primaryContactId,
  primaryContactMode,
  primaryRequiredMessage,
  lockedContactIds = NO_CONTACT_IDS,
  lockedLabel,
  onChange,
  onContactAdded,
  addFormWhenEmpty,
  paging,
  footer,
  layout = 'inline',
  disabled,
  testId,
}: SellerContactsPickerProps) {
  const inModal = layout === 'modal';
  const { t } = useTranslation('general');
  const primaryRequired = primaryContactMode === 'required';
  const lockedIds = new Set(lockedContactIds);
  const alwaysSelectedIds = new Set(lockedIds);
  if (primaryRequired && primaryContactId !== undefined) {
    alwaysSelectedIds.add(primaryContactId);
  }
  const selectedIds = new Set([...selectedContactIds, ...alwaysSelectedIds]);
  const selectedContacts = contacts.filter(({ id: contactId }) =>
    selectedIds.has(contactId)
  );
  const allSelected =
    contacts.length > 0 && selectedContacts.length === contacts.length;
  const someSelected = selectedContacts.length > 0 && !allSelected;
  const listedPrimaryContact =
    primaryContactMode === 'off'
      ? undefined
      : contacts.find(({ id: contactId }) => contactId === primaryContactId);

  const toggleAll = () => {
    if (allSelected) {
      onChange(
        contacts.filter(({ id: contactId }) =>
          alwaysSelectedIds.has(contactId)
        ),
        primaryRequired ? primaryContactId : undefined
      );
    } else {
      onChange(contacts, primaryContactId);
    }
  };

  const withContact = (contact: ContactDetails) =>
    contacts.filter(
      ({ id: contactId }) =>
        selectedIds.has(contactId) || contactId === contact.id
    );

  const toggleContact = (contact: ContactDetails) => {
    if (!selectedIds.has(contact.id)) {
      onChange(withContact(contact), primaryContactId);
      return;
    }
    onChange(
      selectedContacts.filter(({ id: contactId }) => contactId !== contact.id),
      primaryContactId === contact.id ? undefined : primaryContactId
    );
  };

  const changePrimary = (contact: ContactDetails, primary: boolean) =>
    onChange(withContact(contact), primary ? contact.id : undefined);

  const handleContactAdded = (contact: SavedContact) => {
    if (onContactAdded) {
      onContactAdded(contact);
    }
    const primaryMissing =
      primaryRequired && listedPrimaryContact === undefined;
    onChange(
      [...selectedContacts, contact],
      primaryMissing ? contact.id : primaryContactId
    );
  };

  const contactList = (
    <ul
      className={
        inModal
          ? 'grid auto-rows-fr gap-2'
          : 'grid auto-rows-fr grid-cols-1 gap-2 @sm:grid-cols-2 @2xl:grid-cols-3'
      }
    >
      {contacts.map((contact) => (
        <li key={contact.id}>
          <ContactCard
            contact={contact}
            compact={!inModal}
            selected={selectedIds.has(contact.id)}
            onSelectedChange={() => toggleContact(contact)}
            selectDisabled={
              lockedIds.has(contact.id) ||
              (primaryRequired && contact.id === primaryContactId)
            }
            actedLabel={lockedIds.has(contact.id) ? lockedLabel : undefined}
            primary={
              primaryContactMode !== 'off' && contact.id === primaryContactId
            }
            onPrimaryChange={
              primaryContactMode === 'off'
                ? undefined
                : (primary) => changePrimary(contact, primary)
            }
          />
        </li>
      ))}
    </ul>
  );

  const startsEmpty = addFormWhenEmpty !== undefined && contacts.length === 0;

  const pickerBody = (trigger: ReactNode, form: ReactNode) => (
    <>
      {contacts.length === 0 ? (
        <p className="text-sm text-neut-sec">
          {t('select_contacts_modal.no_contacts')}
        </p>
      ) : (
        <>
          <SelectAllCheckbox
            allSelected={allSelected}
            someSelected={someSelected}
            onToggle={toggleAll}
            label={
              paging && paging.hasMore
                ? t('select_contacts_modal.select_all_shown')
                : t('select_contacts_modal.select_all')
            }
            ariaLabel={t('seller_contacts_summary.select_all_aria_label', {
              sellerName,
            })}
            testId={`${testId}-select-all`}
          />
          {paging ? (
            <div
              className={inModal ? undefined : 'max-h-80 overflow-y-auto'}
              data-testid={`${testId}-scroll-area`}
            >
              {contactList}
              <div
                ref={paging.sentinelRef}
                data-testid={`${testId}-sentinel`}
              />
              {paging.isLoadingMore && !paging.loadFailed && (
                <ClipSpinner color="currentColor" />
              )}
              {paging.loadFailed && (
                <LoadFailedBanner onRetry={paging.retry} testId={testId} />
              )}
            </div>
          ) : (
            contactList
          )}
        </>
      )}
      {primaryRequired && listedPrimaryContact === undefined && (
        <p className="text-sm text-neut-sec">{primaryRequiredMessage}</p>
      )}
      {trigger && <div>{trigger}</div>}
      {form}
    </>
  );

  return (
    <fieldset
      id={id}
      disabled={disabled}
      className={pickerClassNames[layout]}
      data-testid={testId}
    >
      <legend className="sr-only">
        {t('seller_contacts_summary.select_contacts_aria_label', {
          sellerName,
        })}
      </legend>
      <AddSellerContactPanel
        organizationId={organizationId}
        testId={`${testId}-add-contact`}
        disabled={disabled}
        initiallyOpen={startsEmpty}
        onAdded={handleContactAdded}
        onCancel={startsEmpty ? addFormWhenEmpty.onCancel : undefined}
      >
        {({ trigger, form }) => (
          <>
            {inModal ? (
              <div className={modalBodyClassName}>
                {pickerBody(trigger, form)}
              </div>
            ) : (
              pickerBody(trigger, form)
            )}
            {!form &&
              footer &&
              footer({
                selectedContacts,
                primaryContact: listedPrimaryContact,
              })}
          </>
        )}
      </AddSellerContactPanel>
    </fieldset>
  );
}

function LoadFailedBanner({
  onRetry,
  testId,
}: Readonly<{ onRetry: () => void; testId: string }>) {
  const { t } = useTranslation('general');
  return (
    <RetryBanner
      message={t('select_contacts_modal.load_failed')}
      retryLabel={t('general.retry')}
      onRetry={onRetry}
      testId={testId}
    />
  );
}

export function PagedSellerContactsPicker(props: ListedContactsPickerProps) {
  const {
    id,
    organizationId,
    selectedContactIds,
    lockedContactIds = NO_CONTACT_IDS,
    primaryContactId,
    onContactAdded,
    testId,
  } = props;
  const {
    contacts,
    status,
    listAdded,
    sentinelRef,
    isLoadingMore,
    hasMore,
    loadFailed,
    retry,
  } = useSellerContactsPage(organizationId, [
    ...selectedContactIds,
    ...lockedContactIds,
    ...(primaryContactId === undefined ? [] : [primaryContactId]),
  ]);

  if (status !== 'loaded') {
    return (
      <div
        id={id}
        className={
          props.layout === 'modal'
            ? modalBodyClassName
            : pickerClassNames.inline
        }
        data-testid={testId}
      >
        {status === 'loading' ? (
          <ClipSpinner color="currentColor" />
        ) : (
          <LoadFailedBanner onRetry={retry} testId={testId} />
        )}
      </div>
    );
  }

  return (
    <SellerContactsPicker
      {...props}
      contacts={contacts}
      paging={{ sentinelRef, isLoadingMore, hasMore, loadFailed, retry }}
      onContactAdded={(contact) => {
        listAdded(contact);
        if (onContactAdded) {
          onContactAdded(contact);
        }
      }}
    />
  );
}
